import React, {cloneElement, ComponentProps, FunctionComponent, useMemo} from 'react';
import {DialogProps} from '@material-ui/core/Dialog';
import {Dialog} from '@material-ui/core';
import {KlDialogAction} from "./klDialogAction.component";
import {KlDialogTitle} from "./klDialogTitle.component";
import {useIsMobile} from "../../hooks/useIsMobile.hook";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./klResponsiveDialog.component.scss');

export type KlResponsiveDialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'huge';

interface KlResponsiveDialogProps extends DialogProps {
    size?: KlResponsiveDialogSize;
    fullHeight?: boolean;
    stickToTop?: boolean;
    scrollBodyClassName?: string;
    stickyAction?: boolean;
}

export const KlResponsiveDialog: FunctionComponent<KlResponsiveDialogProps> = props => {
    const isMobile = useIsMobile();
    const {size, fullHeight, fullScreen, stickToTop, scrollBodyClassName, stickyAction, children, className, ...dialogProps} = props;
    const isFullScreen = useMemo(() => isMobile || fullScreen, [isMobile, fullScreen]);
    const actionChild = useMemo(() => React.Children.toArray(children).find(child => child && typeof child === 'object' &&
        'type' in child && child.type === KlDialogAction), [children])
    const augmentedChildren = useMemo(() =>
        React.Children.map(children, child => {
            if (child && typeof child === 'object' &&
                'type' in child && child.type === KlDialogTitle) { //'in' used to narrow type away from the empty object {}
                const moreProps: Partial<ComponentProps<typeof KlDialogTitle>> = {fullScreen: isFullScreen};
                if (!child.props.onCancel) moreProps.onCancel = props.onClose as (() => void | undefined);
                if (isFullScreen && !isMobile) {
                    moreProps.titleActions = actionChild;
                }
                return cloneElement(child, moreProps);
            } else {
                return child;
            }
        }),
        //onClose ignored on purpose
        [children, isFullScreen, actionChild]);
    const sizeClass = useMemo(() => {
        if (isFullScreen || !size) return '';
        return styles[size];
    }, [isFullScreen, size]);


    return (
        <Dialog fullScreen={isFullScreen} className={`${isFullScreen ? styles.fullScreen : ''} ${isMobile ? styles.mobile : ''} ${stickyAction && styles.stickyAction}`}
                   maxWidth={false}
                   classes={{paper: `${styles.klDialog} ${sizeClass} ${(!isMobile && fullHeight) ? styles.fullHeight : ''} ${className || ''}`,
                       scrollBody: `${styles.scrollBody} ${scrollBodyClassName || ''}`,
                       paperScrollBody: `${stickToTop ? styles.topSticky : ''}`}}
                   {...dialogProps}>
            {augmentedChildren}
        </Dialog>
    );
};
